import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSpaces } from "../context/SpaceProvider";
import {
  useAllSpacesCompanionMessages,
  type TaggedCompanionMessage,
} from "./useAllSpacesCompanionMessages";
import useRetryFetch from "./useRetryFetch";
import { useReflectionDrip, type ReflectionContent } from "./useReflectionDrip";
import {
  listProactiveInsights,
  type ProactiveInsight,
} from "../api/proactiveInsights";
import {
  getDailyQuote,
  getDailyScienceFact,
  getDailyTip,
  getDailyWisdomType,
  PHILOSOPHY_STEPS,
  type Quote,
  type ScienceFact,
  type PlatformTip,
  type PhilosophyStep,
} from "../lib/homeContent";

// ─── Types ───────────────────────────────────────────────────────────────────

export type FeedItemType =
  | "quote"
  | "companion"
  | "wisdom"
  | "engagement_reflection"
  | "proactive_insight"
  | "science"
  | "tip"
  | "philosophy"
  | "cta";

interface FeedItemBase {
  id: string;
  type: FeedItemType;
  timestamp?: number;
}

export interface QuoteFeedItem extends FeedItemBase {
  type: "quote";
  content: Quote;
}

export interface CompanionFeedItem extends FeedItemBase {
  type: "companion";
  content: TaggedCompanionMessage;
  spaceName: string;
  spaceColor: string;
}

export interface WisdomFeedItem {
  id: "daily-wisdom";
  type: "wisdom";
  content:
    | { wisdomType: "quote"; quote: Quote }
    | { wisdomType: "science"; science: ScienceFact }
    | { wisdomType: "tip"; tip: PlatformTip };
}

export interface EngagementReflectionFeedItem extends FeedItemBase {
  type: "engagement_reflection";
  content: { summary: string; activityName?: string };
}

export interface ScienceFeedItem extends FeedItemBase {
  type: "science";
  content: ScienceFact;
}

export interface TipFeedItem extends FeedItemBase {
  type: "tip";
  content: PlatformTip;
}

export interface PhilosophyFeedItem extends FeedItemBase {
  type: "philosophy";
  content: PhilosophyStep;
}

export interface ProactiveInsightFeedItem extends FeedItemBase {
  type: "proactive_insight";
  content: ProactiveInsight;
}

export interface CtaFeedItem extends FeedItemBase {
  type: "cta";
  content: { ctaType: "create_space" | "explore_marketplace" };
}

export type FeedItem =
  | QuoteFeedItem
  | CompanionFeedItem
  | WisdomFeedItem
  | EngagementReflectionFeedItem
  | ProactiveInsightFeedItem
  | ScienceFeedItem
  | TipFeedItem
  | PhilosophyFeedItem
  | CtaFeedItem;

export interface UseGrowthFeedReturn {
  items: FeedItem[];
  isLoading: boolean;
  hasPersonalContent: boolean;
  reflection: {
    content: ReflectionContent;
    answer: (t: string) => Promise<"insight" | "action">;
    skip: () => void;
  } | null;
}

// ─── Wisdom Slot ─────────────────────────────────────────────────────────────
// One rotating wisdom item per day (science / tip / quote) — replaces the old
// always-first quote hero and the static science+tip pair. Standalone so a
// later feed assembly (e.g. a reflection-drip rebuild) can call it directly.

export const buildWisdomItem = (): WisdomFeedItem => {
  const wisdomType = getDailyWisdomType();
  if (wisdomType === "science")
    return { id: "daily-wisdom", type: "wisdom", content: { wisdomType, science: getDailyScienceFact() } };
  if (wisdomType === "tip")
    return { id: "daily-wisdom", type: "wisdom", content: { wisdomType, tip: getDailyTip() } };
  return { id: "daily-wisdom", type: "wisdom", content: { wisdomType: "quote", quote: getDailyQuote() } };
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGrowthFeed(): UseGrowthFeedReturn {
  const retryFetch = useRetryFetch();
  const { hasSpaces } = useSpaces();
  const { messages: companionMessages, isLoading: companionLoading } =
    useAllSpacesCompanionMessages();
  const { content: reflectionContent, answer, skip } = useReflectionDrip();

  // ── Proactive Insights (daily summary + cross-workflow pattern) ──
  const {
    data: proactiveData,
    isLoading: proactiveLoading,
  } = useQuery({
    queryKey: ["home-proactive-insights"],
    queryFn: async () => {
      const data = await listProactiveInsights(retryFetch, 10) as any;
      const insights: ProactiveInsight[] = data.insights || data.list || [];
      const targetTypes = ["progress_summary", "pattern_detected", "weekly_summary", "monthly_summary"];
      return insights.filter(
        (i) => targetTypes.includes(i.insightType) && i.status !== "dismissed"
      );
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // ── Build Feed (interleaved) ──
  const items = useMemo(() => {
    const feed: FeedItem[] = [];
    const hasPersonal = companionMessages.length > 0;

    if (!hasPersonal) {
      // New user feed — keep fixed order for onboarding
      feed.push({
        id: "phil-1",
        type: "philosophy",
        content: PHILOSOPHY_STEPS[0],
      });
      feed.push({
        id: "phil-2",
        type: "philosophy",
        content: PHILOSOPHY_STEPS[1],
      });

      feed.push(buildWisdomItem());

      if (!hasSpaces) {
        feed.push({
          id: "cta-create-space",
          type: "cta",
          content: { ctaType: "create_space" },
        });
      }
    } else {
      // Engaged: one reflection, at most one other AI suggestion, one wisdom slot.
      const nonWelcome = companionMessages.filter((m) => m.messageType !== "welcome");
      const candidates: (CompanionFeedItem | ProactiveInsightFeedItem)[] = [];
      for (const msg of nonWelcome.slice(0, 1)) {
        candidates.push({
          id: `companion-${msg._id}`, type: "companion", content: msg,
          spaceName: msg.spaceName, spaceColor: msg.spaceColor,
          timestamp: new Date(msg.createdOn).getTime(),
        });
      }
      for (const insight of (proactiveData || []).slice(0, 1)) {
        candidates.push({
          id: `proactive-${insight._id}`, type: "proactive_insight",
          content: insight, timestamp: insight.generatedAt,
        });
      }
      candidates.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      if (candidates[0]) feed.push(candidates[0]);
      feed.push(buildWisdomItem());
    }

    return feed;
  }, [
    companionMessages,
    proactiveData,
    hasSpaces,
  ]);

  const hasPersonalContent = companionMessages.length > 0;
  const isLoading = companionLoading || proactiveLoading;

  return {
    items,
    isLoading,
    hasPersonalContent,
    reflection: reflectionContent ? { content: reflectionContent, answer, skip } : null,
  };
}
